import { createLogger } from '@entx/domain-primitives'
import { Form, redirect, useNavigation } from 'react-router'
import { auth } from '../../server/lib/auth-server'
import { isAuthenticationRateLimitFailure } from '../../server/lib/auth-abuse-observability'
import { getOperatorContext } from '../../server/lib/loader-context'
import { parseVenueOperatorSignup } from '../../server/lib/venue-operator-signup'
import { VenueOnboardingShell } from '../components/venue-onboarding-shell'
import { Button, Field, Input } from '../components/ui'
import { VStack, styled } from '../styled-system/jsx'
import type { Route } from './+types/signup'

const logger = createLogger('self-service.venue-operator-signup')

export function meta() {
    return [{ title: 'Create your venue operator account - ENTX' }]
}

export async function loader({ request }: Route.LoaderArgs) {
    const operator = await getOperatorContext(request)
    if (operator) {
        throw redirect(
            operator.user.emailVerified
                ? '/onboarding'
                : `/verify-email?intent=venue&email=${encodeURIComponent(operator.user.email)}`,
        )
    }

    return {}
}

export async function action({ request }: Route.ActionArgs) {
    const parsed = parseVenueOperatorSignup(await request.formData())
    if (!parsed.success) {
        if (parsed.isBot) {
            logger.warn('Venue operator signup rejected by bot protection')
            throw redirect('/verify-email?intent=venue')
        }

        return { fieldErrors: parsed.fieldErrors }
    }

    try {
        await auth.api.signUpEmail({
            headers: request.headers,
            body: {
                ...parsed.data,
                callbackURL: '/onboarding',
            },
        })
        logger.log('Venue operator signup accepted')
    } catch (error) {
        const errorType = error instanceof Error ? error.name : 'unknown'
        if (isAuthenticationRateLimitFailure(error)) {
            logger.warn('Venue operator signup throttled', { errorType })
        } else {
            logger.warn('Venue operator signup returned a generic failure', { errorType })
        }
    }

    throw redirect(`/verify-email?intent=venue&email=${encodeURIComponent(parsed.data.email)}`)
}

export default function Signup({ actionData }: Route.ComponentProps) {
    const navigation = useNavigation()
    const submitting = navigation.state === 'submitting'

    return (
        <VenueOnboardingShell
            title="Create your account"
            description="Use an email address you can verify. Your venue profile will be created after verification."
        >
            <styled.section borderWidth="1px" borderColor="border.subtle" borderRadius="lg" p={{ base: '5', md: '7' }}>
                <Form method="post">
                    <VStack gap="5" alignItems="stretch">
                        <Field.Root invalid={Boolean(actionData?.fieldErrors?.name)}>
                            <Field.Label>Name</Field.Label>
                            <Input name="name" autoComplete="name" required />
                            {actionData?.fieldErrors?.name && <Field.ErrorText>{actionData.fieldErrors.name}</Field.ErrorText>}
                        </Field.Root>

                        <Field.Root invalid={Boolean(actionData?.fieldErrors?.email)}>
                            <Field.Label>Email</Field.Label>
                            <Input name="email" type="email" autoComplete="email" required />
                            {actionData?.fieldErrors?.email && <Field.ErrorText>{actionData.fieldErrors.email}</Field.ErrorText>}
                        </Field.Root>

                        <Field.Root invalid={Boolean(actionData?.fieldErrors?.password)}>
                            <Field.Label>Password</Field.Label>
                            <Input name="password" type="password" autoComplete="new-password" minLength={8} required />
                            <Field.HelperText>Use at least 8 characters.</Field.HelperText>
                            {actionData?.fieldErrors?.password && (
                                <Field.ErrorText>{actionData.fieldErrors.password}</Field.ErrorText>
                            )}
                        </Field.Root>

                        <styled.div position="absolute" left="[-10000px]" aria-hidden="true">
                            <label htmlFor="website">Website</label>
                            <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
                        </styled.div>

                        <Button type="submit" loading={submitting}>
                            Continue
                        </Button>
                        <styled.p fontSize="sm" color="fg.muted" textAlign="center">
                            Already have an account?{' '}
                            <styled.a href="/login?intent=venue" color="accent.default" fontWeight="semibold">
                                Sign in
                            </styled.a>
                        </styled.p>
                    </VStack>
                </Form>
            </styled.section>
        </VenueOnboardingShell>
    )
}
